/* eslint-disable react/jsx-pascal-case */
import React, { memo, useMemo } from 'react';
import { bool, shape, string, arrayOf } from 'prop-types';
import classNames from 'classnames/bind';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { AppContext } from '@thd-nucleus/app-render';
import { CustomerContext, useThdCustomer } from '@thd-olt-functional/customer-information';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  getContentfulTargetingIds,
  useVariationIds,
} from '@thd-olt-component-react/personalization-utils';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { SponsoredUniversalBanner } from '@thd-olt-component-react/sponsored-content';
import { componentMapper } from '../dataModel/componentMapper';
import { NavigationalRenderResolver } from './components/NavigationalRenderResolver';
import { parseContent, parseSponsoredContent } from '../utils/utilities';
import { useGetUrlParameter } from '../hooks/useGetUrlParameter';
import styles from './navigational-layout.scss';
import { previewComponentMapper } from '../dataModel/previewComponentMapper';

/**
 * Main job of this component is to just render the various harmony components only when necessary (see react pure
 * components).
 */
export const NavigationalLayoutRenderer = memo(({
  canonicalURL,
  data,
  filterBreadcrumbs,
  pageId,
  customerType,
  storeId,
  pageTypeComponents,
  search,
  thdSEOBotDetection,
  isPreviewLink,
  isLivePreview = false
}) => {
  declareContexts([AppContext, CustomerContext]);
  const thdCustomer = useThdCustomer();
  const cx = classNames.bind(styles);
  const layouts = data?.layouts;
  const content = layouts?.content;
  const experienceHasSideNav = !!content?.sideNavigation?.id;

  const { mboxes, innervateTags } = getContentfulTargetingIds({ content,
    containerKeys: {
      SpotlightContainer: 'spotlightList'
    } });

  const isReady = mboxes.length > 0 || innervateTags.length > 0;

  // adobe's Campaign Management Multi-Media Channel parameter to be passed to Citi, so that we can
  // track credit card campaigns through application completions - for Spotlight and Hero components
  const cmMmcParam = useGetUrlParameter('cm_mmc');

  const { variationIds, renderDefault } = useVariationIds({
    mboxes,
    innervateTags,
    isReady,
  });

  const theme = useTheme(NavigationalLayoutRenderer);
  const { hideInternalMarketingBanners } = theme.props;

  // This logic appends '_b2b' to the pageId for B2B customer types so the EndCap component can render for both B2B and B2C at the same time
  const endCapPageUrl = customerType.toLowerCase() === 'b2b'
    ? `${pageId}_b2b`
    : pageId;

  const customProps = {
    ComponentSelector: (component) => ({
      componentMap: isLivePreview ? previewComponentMapper.getComponentMap() : componentMapper.getComponentMap(),
      defaultComponent: component.defaultComponent,
      variations: component.variations,
      variationIds: variationIds || [],
      renderDefault,
      isSeoBot: thdSEOBotDetection,
      targetingType: component.targetingType,
      customPropMap: {
        Spotlight: {
          showDescription: true,
          cmMmcParam
        },
        SpotlightContainer: {
          showDescription: true,
          cmMmcParam
        },
        HeroPersonalized: {
          thdPageId: pageId,
          searchParams: search,
          isSeoBot: thdSEOBotDetection,
          customerType
        },
        Hero: {
          hasSidenav: true,
          cmMmcParam
        },
      }
    }),
    EndCap: () => ({
      customerType: customerType.toUpperCase(),
      pageUrl: `/${endCapPageUrl}`
    }),
    SeoLinks: () => ({
      path: canonicalURL
    }),
    Spotlight: () => ({
      showDescription: true,
      cmMmcParam,
      shouldUseContentPlayer: true
    }),
    SpotlightContainer: () => ({
      showDescription: true,
      cmMmcParam,
      variationIds: variationIds || [],
      renderDefault,
      isSeoBot: thdSEOBotDetection,
    }),
    HeroPersonalized: () => ({
      thdPageId: pageId,
      searchParams: search,
      isSeoBot: thdSEOBotDetection,
      customerType
    }),
    Hero: () => ({
      cmMmcParam
    }),
    Resource: () => ({
      shouldUseContentPlayer: true
    }),
    PromoVisualNavigation: () => ({
      experienceHasSideNav: true,
      enableTheme: true
    }),
    ProductShelf: () => ({
      showTooFewProductsNotice: isPreviewLink
    }),
    VisualNavigation: () => ({
      enableTheme: true
    }),
  };

  const { Content } = useMemo(() => parseContent({
    data,
    componentMapper: isLivePreview ? previewComponentMapper : componentMapper,
    customProps,
    layout: 'navigational',
  }), [data, variationIds, renderDefault, search]);

  const { Sponsored } = useMemo(() => parseSponsoredContent({
    data,
    componentMapper: isLivePreview ? previewComponentMapper : componentMapper,
    customProps,
    layout: 'navigational',
  }), [data]);

  const pageTitle = content?.title;
  const descriptionText = content?.descriptionNew?.descriptionText;
  const descriptionRichText = content?.descriptionNew?.description;
  const descriptionTitle = content?.descriptionNew?.title || '';
  const bottomCarouselCustomRenderId = layouts?.content?.sponsoredBottomCarousel?.customRenderId;
  const middleBannerCustomRenderId = content?.sponsoredMiddleBanner?.customRenderId;
  const skyscraperBannerCustomRenderId = layouts?.content?.sponsoredSkyscraperBanner?.customRenderId;
  const topBannerCustomRenderId = layouts?.content?.sponsoredTopBanner?.customRenderId;
  const sponsoredBrowserId = thdCustomer?.mcvisID || '';
  const isCategoryPage = layouts?.type === 'Category';

  let contentfulDefaultBanner = {};

  if (content?.sponsoredMiddleBanner !== null) {
    contentfulDefaultBanner = content?.sponsoredMiddleBanner;
  } else if (content?.sponsoredTopBanner !== null) {
    contentfulDefaultBanner = content?.sponsoredTopBanner;
  }

  const { landingPageBreadcrumbs, landingPageMetadata } = pageTypeComponents;

  return (
    <Row name="navigational-layout-manager">
      <ErrorBoundary name="metadata">
        {landingPageMetadata}
      </ErrorBoundary>
      <Row className="isBound">
        <Col xs="12" className={cx('navigational-layout__no-bottom-padding')}>
          <ErrorBoundary name="breadcrumbs">
            {landingPageBreadcrumbs}
          </ErrorBoundary>
        </Col>
        <Col xs="12" className={cx('navigational-layout__no-top-bottom-padding')}>
          <ErrorBoundary name="sponsored-top-banner">
            <Sponsored.sponsoredTopBanner
              browserId={sponsoredBrowserId}
              slugId={pageId}
              customRenderId={topBannerCustomRenderId}
              contentfulDefaultBanner={contentfulDefaultBanner}
              schema="cat_sponsored"
            />
          </ErrorBoundary>
        </Col>
        <Col xs="12">
          {pageTitle && (
            <ErrorBoundary name="heading">
              <Typography
                variant="h1"
                weight="display"
                height="snug"
                color="primary"
                uppercase
                data-contentful-entry-id={layouts?.content?.id}
                data-contentful-field-id="title"
              >
                {pageTitle}
              </Typography>
            </ErrorBoundary>
          )}
          <Row className={`isBound ${!experienceHasSideNav && ' sui-justify-center'}`}>
            {experienceHasSideNav && (
              <Col sm="2" md="2" lg="2">
                <ErrorBoundary name="side-navigation">
                  <div className={cx('navigational-layout__side-navigation')}>
                    <Content.sideNavigation />
                  </div>
                </ErrorBoundary>
                {isCategoryPage && (
                  <ErrorBoundary name="sponsored-skyscraper-banner">
                    <Sponsored.sponsoredSkyscraperBanner
                      browserId={sponsoredBrowserId}
                      slugId={pageId}
                      customRenderId={skyscraperBannerCustomRenderId}
                      schema="cat_sponsored"
                    />
                  </ErrorBoundary>
                )}
              </Col>
            )}
            <Col xs="12" sm="10" md="10" lg="10">
              <ErrorBoundary name="hero-carousel">
                <Content.heroCarousel />
              </ErrorBoundary>
              <NavigationalRenderResolver componentId="promo-visual-navigation-1">
                <Content.promoVisualNavigation1 />
              </NavigationalRenderResolver>

              <NavigationalRenderResolver componentId="promo-visual-navigation-2">
                <Content.promoVisualNavigation2 />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="promo-visual-navigation-3">
                <Content.promoVisualNavigation3 />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="endcap">
                <Content.endCap />
              </NavigationalRenderResolver>
              {!hideInternalMarketingBanners && (
                <NavigationalRenderResolver componentId="internal-marketing-banner-1">
                  <Content.internalMarketingBanner1 />
                </NavigationalRenderResolver>
              )}
              <NavigationalRenderResolver componentId="visual-navigation-1">
                <Content.visualNavigation1 />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="visual-navigation-2">
                <Content.visualNavigation2 />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="visual-navigation-3">
                <Content.visualNavigation3 />
              </NavigationalRenderResolver>
              {!hideInternalMarketingBanners && (
                <NavigationalRenderResolver componentId="internal-marketing-banner-2">
                  <Content.internalMarketingBanner2 />
                </NavigationalRenderResolver>
              )}
              <NavigationalRenderResolver componentId="special-buy-banner">
                <Content.specialBuyOfTheDay specialBuyType="sbotd" />
              </NavigationalRenderResolver>
              {!hideInternalMarketingBanners && (
                <NavigationalRenderResolver componentId="internal-marketing-banner-3">
                  <Content.internalMarketingBanner3 />
                </NavigationalRenderResolver>
              )}
              <NavigationalRenderResolver componentId="product-shelf">
                <Content.productShelf />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="spotlight">
                <Content.spotlightSection slidesLg={2} slidesXl={3} slidesMd={2} slidesSm={1} />
              </NavigationalRenderResolver>

              {!hideInternalMarketingBanners && (
                <NavigationalRenderResolver componentId="medio-inline-1">
                  <Content.medioInline1 />
                </NavigationalRenderResolver>
              )}
              {!hideInternalMarketingBanners && (
                <NavigationalRenderResolver componentId="piccola-pencil-1">
                  <Content.piccolaPencil1 />
                </NavigationalRenderResolver>
              )}
              <NavigationalRenderResolver componentId="visual-navigation-4">
                <Content.visualNavigation4 />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="visual-navigation-5">
                <Content.visualNavigation5 />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="visual-navigation-6">
                <Content.visualNavigation6 />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="sponsored-middle-banner">
                <Sponsored.sponsoredMiddleBanner
                  slugId={pageId}
                  customRenderId={middleBannerCustomRenderId}
                  contentfulDefaultBanner={contentfulDefaultBanner}
                  schema="cat_sponsored"
                />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="resource">
                <Content.resourcesSection />
              </NavigationalRenderResolver>
              {isCategoryPage && (
                <NavigationalRenderResolver componentId="sponsored-leaderboard-banner">
                  <SponsoredUniversalBanner
                    bannerType="leaderboard"
                    nValue={pageId}
                    schema="cat_sponsored"
                  />
                </NavigationalRenderResolver>
              )}
              {!hideInternalMarketingBanners && (
                <NavigationalRenderResolver componentId="medio-inline-2">
                  <Content.medioInline2 />
                </NavigationalRenderResolver>
              )}
              {!hideInternalMarketingBanners && (
                <NavigationalRenderResolver componentId="piccola-pencil-2">
                  <Content.piccolaPencil2 />
                </NavigationalRenderResolver>
              )}
              {Content.dynamicSections?.map((SubSection, key) => (
                <NavigationalRenderResolver
                  componentId={`dynamic-section-${key}`}
                  key={`dynamic-section-${key}`}
                >
                  <SubSection />
                </NavigationalRenderResolver>
              ))}
              {!hideInternalMarketingBanners && (
                <NavigationalRenderResolver componentId="medio-inline-3">
                  <Content.medioInline3 />
                </NavigationalRenderResolver>
              )}
              {!hideInternalMarketingBanners && (
                <NavigationalRenderResolver componentId="piccola-pencil-3">
                  <Content.piccolaPencil3 />
                </NavigationalRenderResolver>
              )}
              <NavigationalRenderResolver componentId="sponsored-bottom-carousel">
                <Sponsored.sponsoredBottomCarousel
                  plaLocation="sponsoredCarouselLandingPage"
                  browserId={sponsoredBrowserId}
                  breadCrumbs={filterBreadcrumbs}
                  customRenderId={bottomCarouselCustomRenderId}
                  pageContext={{
                    schema: 'cat_sponsored'
                  }}
                  nValue={pageId}
                />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="description">
                <Content.descriptionNew title={descriptionTitle} description={descriptionText} descriptionRichText={descriptionRichText} />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="related-search">
                <Content.relatedSearchesAndProducts navParam={pageId} />
              </NavigationalRenderResolver>
              <NavigationalRenderResolver componentId="seo-links">
                {canonicalURL && <Content.seoLinks />}
              </NavigationalRenderResolver>
            </Col>
          </Row>
        </Col>
      </Row>
    </Row>
  );
});

NavigationalLayoutRenderer.displayName = 'NavigationalLayoutRenderer';

NavigationalLayoutRenderer.propTypes = {
  canonicalURL: string,
  data: shape({}).isRequired,
  filterBreadcrumbs: arrayOf(shape(
    {
      browseUrl: string,
      label: string,
    },
  )),
  pageTypeComponents: shape({}).isRequired,
  pageId: string.isRequired,
  search: string,
  storeId: string.isRequired,
  customerType: string.isRequired,
  thdSEOBotDetection: bool,
  isPreviewLink: bool,
  isLivePreview: bool
};

NavigationalLayoutRenderer.defaultProps = {
  canonicalURL: null,
  filterBreadcrumbs: null,
  search: '',
  thdSEOBotDetection: false,
  isPreviewLink: false
};

NavigationalLayoutRenderer.themeProps = {
  hideInternalMarketingBanners: bool,
};

NavigationalLayoutRenderer.defaultThemeProps = {
  hideInternalMarketingBanners: false
};
